/**
 * MarketingPanel — budget & channel allocation with live CAC estimates.
 */

import { useState } from 'react';
import { toast } from '@/hooks/useToast';
import { Megaphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Meter, PanelSection } from '@/components/game/ui';
import { useGameStore } from '@/store/gameStore';
import { money, num, pct } from '@/game/format';
import { MARKETING_CHANNELS } from '@/game/constants';
import { cacEffective } from '@/game/economy';
import type { MarketingChannelId } from '@/game/types';

export function MarketingPanel() {
  const state = useGameStore((s) => s.state)!;
  const setMarketing = useGameStore((s) => s.setMarketing);

  const [budget, setBudget] = useState(state.marketing.dailyBudget);
  const [enabled, setEnabled] = useState(state.marketing.enabled);
  const [alloc, setAlloc] = useState<Record<MarketingChannelId, number>>({ ...state.marketing.allocation });
  // Local edit state initializes on mount (tab switch) — deliberately NOT
  // re-synced every tick, since the engine clones state at high frequency.

  const totalAlloc = Object.values(alloc).reduce((a, b) => a + b, 0);

  const projected = MARKETING_CHANNELS.map((def) => {
    const spend = budget * (alloc[def.id] ?? 0);
    const eff = def.saturation * Math.tanh(spend / def.saturation);
    const cac = cacEffective(state, def.id);
    return { id: def.id, name: def.name, spend, orders: spend > 0 ? eff / cac : 0, cac, fatigue: state.marketing.fatigue[def.id], notes: def.notes };
  });

  const save = () => {
    setMarketing(budget, alloc as typeof state.marketing.allocation, enabled);
    toast({ title: 'Marketing plan updated', description: `${money(budget)}/day across ${Object.values(alloc).filter((v) => v > 0.001).length} channels.` });
  };

  return (
    <div className="space-y-6">
      <PanelSection title="Daily Budget" desc="CACs inflate during Q4 auctions and competitive bloodbaths. Brand awareness is the only lasting discount.">
        <Card>
          <CardContent className="space-y-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-1 items-center gap-3">
                <Megaphone className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Input
                  type="number"
                  min={0}
                  step={50}
                  value={budget}
                  onChange={(e) => setBudget(Math.max(0, Number(e.target.value)))}
                  className="w-32"
                  aria-label="Daily ad budget"
                />
                <span className="text-sm text-muted-foreground">per day</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Ads enabled" />
                <Label className="text-sm">{enabled ? 'Running' : 'Paused'}</Label>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Email list: {num(state.marketing.emailListSize)} · Returning pool: {num(state.marketing.returningCustomerPool)} ·
              Awareness decays ~{pct(0.02 * (state.player.awareness / 25), 1)}/day without spend.
            </p>
          </CardContent>
        </Card>
      </PanelSection>

      <PanelSection
        title="Channel Mix"
        desc={`Allocated ${pct(totalAlloc, 0)} of budget${totalAlloc > 1 ? ' — will be normalized' : ''}.`}
        right={
          <Button size="sm" onClick={save} disabled={totalAlloc > 1.001}>
            Apply plan
          </Button>
        }
      >
        <div className="space-y-3">
          {projected.map((ch) => (
            <Card key={ch.id}>
              <CardContent className="space-y-2 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium">{ch.name}</p>
                  <p className="text-xs tabular-nums text-muted-foreground">
                    {money(ch.spend)}/day → ~{ch.orders.toFixed(1)} orders/day @ {money(ch.cac)} CAC
                  </p>
                </div>
                <Slider
                  value={[Math.round((alloc[ch.id as MarketingChannelId] ?? 0) * 100)]}
                  min={0}
                  max={100}
                  step={5}
                  onValueChange={(v) => setAlloc((a) => ({ ...a, [ch.id]: v[0] / 100 }))}
                  aria-label={`${ch.name} allocation`}
                />
                <Meter label="Creative fatigue" value={ch.fatigue * 100} colorClass={cn(ch.fatigue > 0.6 ? 'bg-red-500' : 'bg-amber-500')} valueText={pct(ch.fatigue, 0)} />
                <p className="text-xs text-muted-foreground">{ch.notes}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </PanelSection>
    </div>
  );
}
